/// <reference lib="dom" />
/**
 * The runtime loop (brief §5.3): accumulator fixed timestep at 60Hz, max 5
 * catch-up steps per frame, input sampled once per tick and appended to the
 * run log, rAF-driven rendering with interpolation alpha.
 *
 * Pause semantics: on visibilitychange-hidden the loop stops stepping AND
 * stops appending — the frame index stays equal to the tick index. The resume
 * tick carries PAUSE_MARKER, so a pause is a flag, never a gap. Timed games'
 * clocks stop naturally because elapsed time IS the tick count.
 */

import type { ChoicePoint, InputFrame } from '@hnplay/shared';
import { INPUT_BIT_PAUSE_MARKER, isValidFrame } from '@hnplay/shared';
import type { GameSim } from './pure/sim-contract.js';
import type { GameView } from './view-contract.js';

export const TICK_RATE = 60;
export const TICK_MS = 1000 / TICK_RATE;
export const MAX_CATCHUP_STEPS = 5;

export interface InputSource {
  /** Current tick's frame. Called exactly once per tick, in tick order. */
  sample(): InputFrame;
}

export interface LoopHooks {
  onChoiceOpen?(cp: ChoicePoint): void;
  onChoiceClosed?(): void;
  onOver?(): void;
  onPause?(): void;
  onResume?(): void;
}

export interface GameLoop {
  start(): void;
  stop(): void;
  readonly running: boolean;
  /** The recorded run log — array index === tick index. */
  getFrames(): InputFrame[];
  getTick(): number;
}

export function createGameLoop<TState>(opts: {
  sim: GameSim<TState>;
  view: GameView<TState>;
  input: InputSource;
  hooks?: LoopHooks;
  /** Injectable for tests. Defaults to page visibility. */
  doc?: Document | null;
}): GameLoop {
  const { sim, view, input } = opts;
  const hooks = opts.hooks ?? {};
  const doc = opts.doc === undefined ? (typeof document !== 'undefined' ? document : null) : opts.doc;

  const frames: InputFrame[] = [];
  let raf = 0;
  let running = false;
  let last = 0;
  let acc = 0;
  let pendingPauseMark = false;
  let choiceWasOpen = false;
  let over = false;

  function stepOnce(): void {
    let frame = input.sample();
    if (pendingPauseMark) {
      frame = { ...frame, bits: frame.bits | INPUT_BIT_PAUSE_MARKER };
      pendingPauseMark = false;
    }
    if (!isValidFrame(frame.bits, frame.axisX, frame.axisY)) {
      // A buggy input source must corrupt neither the log nor the sim.
      frame = { bits: frame.bits & 0xffff & ~0, axisX: 0, axisY: 0 };
      if (!isValidFrame(frame.bits, 0, 0)) frame = { bits: 0, axisX: 0, axisY: 0 };
    }
    frames.push(frame);
    sim.step(frame);

    const cp = sim.getChoicePoint();
    if (cp && !choiceWasOpen) hooks.onChoiceOpen?.(cp);
    if (!cp && choiceWasOpen) hooks.onChoiceClosed?.();
    choiceWasOpen = cp !== null;

    if (sim.isOver() && !over) {
      over = true;
      hooks.onOver?.();
    }
  }

  function frameCb(now: number): void {
    if (!running) return;
    raf = requestAnimationFrame(frameCb);
    const dt = Math.min(now - last, 250);
    last = now;
    acc += dt;
    let steps = 0;
    while (acc >= TICK_MS && steps < MAX_CATCHUP_STEPS && !over) {
      stepOnce();
      acc -= TICK_MS;
      steps += 1;
    }
    if (steps === MAX_CATCHUP_STEPS) acc = 0; // shed backlog: slow devices play slow-motion, never spiral
    const alpha = over ? 1 : Math.min(Math.max(acc / TICK_MS, 0), 1);
    view.render(sim.getPrevState(), sim.getState(), alpha);
  }

  function onVisibility(): void {
    if (!doc) return;
    if (doc.visibilityState === 'hidden') {
      if (running) {
        cancelAnimationFrame(raf);
        running = false;
        hooks.onPause?.();
      }
    } else if (!over) {
      pendingPauseMark = true;
      startInternal();
      hooks.onResume?.();
    }
  }

  function startInternal(): void {
    if (running) return;
    running = true;
    last = performance.now();
    acc = 0;
    raf = requestAnimationFrame(frameCb);
  }

  return {
    start(): void {
      doc?.addEventListener('visibilitychange', onVisibility);
      startInternal();
    },
    stop(): void {
      doc?.removeEventListener('visibilitychange', onVisibility);
      cancelAnimationFrame(raf);
      running = false;
    },
    get running(): boolean {
      return running;
    },
    getFrames(): InputFrame[] {
      return frames;
    },
    getTick(): number {
      return frames.length;
    },
  };
}